import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Save, Upload, Globe, Mail, Phone, MapPin,
  Image, Link2, Loader2, Eye, Palette, X, Sparkles, Check,
  TrendingUp, ToggleLeft, ToggleRight, Camera, FileText, Trash2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Predefined theme presets
const THEME_PRESETS = {
  navy: {
    name: 'Navy Professional',
    description: 'Classic navy blue theme with professional feel',
    colors: {
      primary_color: '#1e3a5f',
      secondary_color: '#3d5a80',
      accent_color: '#ee6c4d',
      sidebar_bg: '#1e3a5f',
      sidebar_text: '#e0e1dd',
      sidebar_link_active: '#0d1b2a',
      sidebar_link_hover: '#2d4a6f',
      external_header_bg: '#FFFFFF',
      external_header_text: '#1e3a5f',
      external_page_bg: '#f8f9fa',
      external_footer_bg: '#1e3a5f',
      external_footer_text: '#e0e1dd'
    }
  },
  blue: {
    name: 'Modern Blue',
    description: 'Fresh blue theme with vibrant accents',
    colors: {
      primary_color: '#2563eb',
      secondary_color: '#3b82f6',
      accent_color: '#f59e0b',
      sidebar_bg: '#1e40af',
      sidebar_text: '#dbeafe',
      sidebar_link_active: '#1e3a8a',
      sidebar_link_hover: '#2563eb',
      external_header_bg: '#FFFFFF',
      external_header_text: '#1e40af',
      external_page_bg: '#eff6ff',
      external_footer_bg: '#FFFFFF',
      external_footer_text: '#64748b'
    }
  },
  green: {
    name: 'Forest Green',
    description: 'Natural green theme with earthy tones',
    colors: {
      primary_color: '#166534',
      secondary_color: '#22c55e',
      accent_color: '#eab308',
      sidebar_bg: '#14532d',
      sidebar_text: '#dcfce7',
      sidebar_link_active: '#052e16',
      sidebar_link_hover: '#166534',
      external_header_bg: '#FFFFFF',
      external_header_text: '#166534',
      external_page_bg: '#f0fdf4',
      external_footer_bg: '#FFFFFF',
      external_footer_text: '#64748b'
    }
  },
  gray: {
    name: 'Elegant Gray',
    description: 'Sophisticated gray theme with subtle contrast',
    colors: {
      primary_color: '#374151',
      secondary_color: '#6b7280',
      accent_color: '#8b5cf6',
      sidebar_bg: '#1f2937',
      sidebar_text: '#f3f4f6',
      sidebar_link_active: '#111827',
      sidebar_link_hover: '#374151',
      external_header_bg: '#FFFFFF',
      external_header_text: '#1f2937',
      external_page_bg: '#f9fafb',
      external_footer_bg: '#FFFFFF',
      external_footer_text: '#6b7280'
    }
  },
  light: {
    name: 'Clean Light',
    description: 'Bright, airy theme with light sidebar',
    colors: {
      primary_color: '#0ea5e9',
      secondary_color: '#64748b',
      accent_color: '#f43f5e',
      sidebar_bg: '#f1f5f9',
      sidebar_text: '#334155',
      sidebar_link_active: '#cbd5e1',
      sidebar_link_hover: '#e2e8f0',
      external_header_bg: '#FFFFFF',
      external_header_text: '#334155',
      external_page_bg: '#FFFFFF',
      external_footer_bg: '#f8fafc',
      external_footer_text: '#64748b'
    }
  }
};

const CompanySettings = () => {
  const { userTheme, refreshTheme } = useTheme();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = ['admin', 'main_admin', 'company_admin'].includes(user?.role);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Theme preset modal state
  const [previewTheme, setPreviewTheme] = useState(null);
  const [showThemeReminder, setShowThemeReminder] = useState(false);
  
  // Dynamic theme presets from API
  const [themePresets, setThemePresets] = useState({});
  
  // Profile photos & documents state
  const [profilePhotos, setProfilePhotos] = useState([]);
  const [profileDocuments, setProfileDocuments] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  // Company settings state (for Opportunities tab)
  const [companyFeatureSettings, setCompanyFeatureSettings] = useState({
    show_trados_opportunities: false,
    show_super_admin_videos: true  // Default to true for new companies
  });

  // Get theme values
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    phone: '',
    address: '',
    website: ''
  });

  // Theme settings state
  const [themeSettings, setThemeSettings] = useState({
    primary_color: '#2B4B6A',
    secondary_color: '#64748b',
    accent_color: '#3b82f6',
    sidebar_bg: '#1e293b',
    sidebar_text: '#e2e8f0',
    sidebar_link_active: '#1E3A52',
    sidebar_link_hover: '#3D5F80',
    // External pages (login, registration, etc.)
    external_header_bg: '#FFFFFF',
    external_header_text: '#1F2937',
    external_page_bg: '#F9FAFB',
    external_footer_bg: '#FFFFFF',
    external_footer_text: '#6B7280'
  });

  useEffect(() => {
    fetchCompanySettings();
    fetchThemePresets();
  }, []);
  
  const fetchThemePresets = async () => {
    try {
      const res = await api.get('/theme-presets');
      setThemePresets(res.data);
    } catch (error) {
      console.error('Failed to fetch theme presets:', error);
      // Fallback to hardcoded presets if API fails
      setThemePresets(THEME_PRESETS);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const res = await api.get('/company/settings');
      const data = res.data;
      setCompany(data);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        contact_email: data.contact_email || '',
        phone: data.phone || '',
        address: data.address || '',
        website: data.website || ''
      });
      // Set theme settings from company data
      if (data.theme_settings) {
        setThemeSettings({
          primary_color: data.theme_settings.primary_color || '#2B4B6A',
          secondary_color: data.theme_settings.secondary_color || '#64748b',
          accent_color: data.theme_settings.accent_color || '#3b82f6',
          sidebar_bg: data.theme_settings.sidebar_bg || '#1e293b',
          sidebar_text: data.theme_settings.sidebar_text || '#e2e8f0',
          sidebar_link_active: data.theme_settings.sidebar_link_active || '#1E3A52',
          sidebar_link_hover: data.theme_settings.sidebar_link_hover || '#3D5F80',
          // External pages
          external_header_bg: data.theme_settings.external_header_bg || '#FFFFFF',
          external_header_text: data.theme_settings.external_header_text || '#1F2937',
          external_page_bg: data.theme_settings.external_page_bg || '#F9FAFB',
          external_footer_bg: data.theme_settings.external_footer_bg || '#FFFFFF',
          external_footer_text: data.theme_settings.external_footer_text || '#6B7280'
        });
      }
      // Load feature settings
      if (data.settings) {
        setCompanyFeatureSettings({
          show_trados_opportunities: data.settings.show_trados_opportunities || false,
          show_super_admin_videos: data.settings.show_super_admin_videos !== false  // Default to true
        });
      }
      setLogoPreview(data.logo_url);
      setProfilePhotos(data.profile_photos || []);
      setProfileDocuments(data.profile_documents || []);
    } catch (error) {
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      await api.put('/company/settings', formData);
      toast.success('Company settings saved');
      fetchCompanySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatureSettings = async () => {
    setSaving(true);
    try {
      await api.put('/company/settings/features', { settings: companyFeatureSettings });
      toast.success('Opportunities settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      await api.put('/company/settings/theme', { theme_settings: themeSettings });
      toast.success('Theme settings saved! Refreshing page...');
      refreshTheme();
      setShowThemeReminder(false);
      // Auto-refresh the page after a short delay to show the toast
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme');
      setSaving(false);
    }
  };

  // Apply a preset theme (works with both static and dynamic presets)
  const applyPresetTheme = (presetKey) => {
    const preset = themePresets[presetKey] || THEME_PRESETS[presetKey];
    if (preset) {
      setThemeSettings(prev => ({
        ...prev,
        ...preset.colors
      }));
      setPreviewTheme(null);
      setShowThemeReminder(true);
      toast.info('Theme applied! Click "Save Theme" to save your changes.');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, WebP, GIF, or SVG)');
      return;
    }

    // Validate file size (2MB limit for logos)
    const MAX_LOGO_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_LOGO_SIZE) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 2MB.`);
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/company/settings/logo', formData);

      setLogoPreview(`${res.data.logo_url}?t=${Date.now()}`);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload error:', error);
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Upload timed out. Please try again with a smaller file.');
      } else {
        toast.error('Failed to upload logo. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const handlePhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (profilePhotos.length + files.length > 10) {
      toast.error(`Maximum 10 photos allowed. You have ${profilePhotos.length}, trying to add ${files.length}.`);
      return;
    }

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" is not an image file.`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds the 10MB limit.`);
        return;
      }
    }

    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await api.post('/company/settings/photos', formData);
      setProfilePhotos(res.data.photos);
      toast.success(`Uploaded ${files.length} photo(s)`);
      if (res.data.errors) {
        res.data.errors.forEach(err => toast.error(`${err.file}: ${err.error}`));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (storedName) => {
    try {
      const res = await api.delete(`/company/settings/photos/${storedName}`);
      setProfilePhotos(res.data.photos);
      toast.success('Photo deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const handleDocumentsUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (profileDocuments.length + files.length > 10) {
      toast.error(`Maximum 10 documents allowed. You have ${profileDocuments.length}, trying to add ${files.length}.`);
      return;
    }

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds the 10MB limit.`);
        return;
      }
    }

    setUploadingDocuments(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await api.post('/company/settings/documents', formData);
      setProfileDocuments(res.data.documents);
      toast.success(`Uploaded ${files.length} document(s)`);
      if (res.data.errors) {
        res.data.errors.forEach(err => toast.error(`${err.file}: ${err.error}`));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
      e.target.value = '';
    }
  };

  const handleDeleteDocument = async (storedName) => {
    try {
      const res = await api.delete(`/company/settings/documents/${storedName}`);
      setProfileDocuments(res.data.documents);
      toast.success('Document deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Build full logo URL
  const getLogoUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: primaryColor }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Company Settings</h1>
              <p className="text-gray-500 text-sm">
                {isAdmin ? "Manage your company's profile, branding, and theme" : "View your company's profile and branding"}
              </p>
            </div>
          </div>
        </div>

        {/* Read-only notice for non-admins */}
        {!isAdmin && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-blue-800 text-sm">
              You are viewing your company's profile in read-only mode. Contact your company admin to make changes.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
              activeTab === 'general' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            General Info
          </button>
          <button
            onClick={() => setActiveTab('branding')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
              activeTab === 'branding' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Branding
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('theme')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                activeTab === 'theme'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              Theme Colors
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('opportunities')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                activeTab === 'opportunities'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid="opportunities-tab"
            >
              Opportunities
            </button>
          )}
          <button
            onClick={() => setActiveTab('media')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
              activeTab === 'media'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
            data-testid="media-tab"
          >
            Media
          </button>
        </div>

        {/* General Info Tab */}
        {activeTab === 'general' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Company Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                  data-testid="company-name-input"
                  readOnly={!isAdmin}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                    readOnly={!isAdmin}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                    readOnly={!isAdmin}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                    placeholder="https://"
                    readOnly={!isAdmin}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                    rows={2}
                    readOnly={!isAdmin}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={`w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 ${!isAdmin ? 'bg-gray-50 text-gray-600' : ''}`}
                  rows={3}
                  placeholder="Brief description of your company..."
                  readOnly={!isAdmin}
                />
              </div>
            </div>

            {isAdmin && (
              <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
                <button
                  onClick={handleSaveGeneral}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="save-general-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Company Branding</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Company Logo</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  {logoPreview ? (
                    <div className="mb-4">
                      <img 
                        src={getLogoUrl(logoPreview)} 
                        alt="Company Logo" 
                        className="max-h-32 mx-auto object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Image className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  
                  {isAdmin && (
                    <>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition">
                        <Upload className="w-4 h-4" />
                        {uploadingLogo ? 'Uploading...' : (logoPreview ? 'Change Logo' : 'Upload Logo')}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-2">PNG, JPG up to 2MB. Recommended: 200x200px</p>
                    </>
                  )}
                </div>
              </div>

              {/* Subdomain Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Your Subdomain</label>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-gray-900 notranslate" translate="no">{company?.subdomain}.trados.app</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    This is your unique company URL. Users can access your company portal at this address.
                  </p>
                  <a 
                    href={`https://${company?.subdomain}.trados.app`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-sm text-blue-600 hover:underline notranslate"
                    translate="no"
                  >
                    <Eye className="w-4 h-4" />
                    Preview your portal
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Theme Colors Tab */}
        {activeTab === 'theme' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Theme Colors</h2>
                <p className="text-gray-500 text-sm">Customize the colors used in your company's portal</p>
              </div>
            </div>

            {/* Save Reminder Banner */}
            {showThemeReminder && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-amber-800 font-medium">Theme preset applied!</p>
                  <p className="text-amber-700 text-sm">Don't forget to click <strong>"Save Theme"</strong> below to apply your changes.</p>
                </div>
                <button 
                  onClick={() => setShowThemeReminder(false)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Quick Theme Presets */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Quick Theme Presets
              </h3>
              <p className="text-xs text-gray-500 mb-4">Click a theme to preview, then apply to fill in all colors at once</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(Object.keys(themePresets).length > 0 ? themePresets : THEME_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => setPreviewTheme(key)}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all hover:scale-105"
                    style={{
                      backgroundColor: preset.colors.sidebar_bg,
                      color: preset.colors.sidebar_text,
                      borderColor: preset.colors.primary_color
                    }}
                    data-testid={`theme-preset-${key}`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.primary_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeSettings.primary_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.secondary_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeSettings.secondary_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.accent_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeSettings.accent_color}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Background</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.sidebar_bg}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_bg: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeSettings.sidebar_bg}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_bg: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Text</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.sidebar_text}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_text: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeSettings.sidebar_text}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_text: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Link Active/Selected</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.sidebar_link_active}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_link_active: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    data-testid="sidebar-link-active-color"
                  />
                  <input
                    type="text"
                    value={themeSettings.sidebar_link_active}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_link_active: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sidebar Link Hover</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeSettings.sidebar_link_hover}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_link_hover: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    data-testid="sidebar-link-hover-color"
                  />
                  <input
                    type="text"
                    value={themeSettings.sidebar_link_hover}
                    onChange={(e) => setThemeSettings(prev => ({ ...prev, sidebar_link_hover: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>

            {/* External Pages Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-900">External Pages</h3>
                <p className="text-gray-500 text-sm">Customize the colors for login, registration, and other public pages</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Header Background</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.external_header_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_header_bg: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.external_header_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_header_bg: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Header Text</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.external_header_text}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_header_text: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.external_header_text}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_header_text: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Page Background</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.external_page_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_page_bg: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.external_page_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_page_bg: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Footer Background</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.external_footer_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_footer_bg: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.external_footer_bg}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_footer_bg: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeSettings.external_footer_text}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_footer_text: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={themeSettings.external_footer_text}
                      onChange={(e) => setThemeSettings(prev => ({ ...prev, external_footer_text: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-8 p-6 bg-gray-100 rounded-xl">
              <h3 className="font-medium text-gray-900 mb-4">Preview</h3>
              <div className="flex gap-4">
                <div 
                  className="w-48 h-40 rounded-lg flex flex-col"
                  style={{ backgroundColor: themeSettings.sidebar_bg }}
                >
                  <div className="p-3">
                    <div className="text-sm font-medium" style={{ color: themeSettings.sidebar_text }}>
                      Sidebar Preview
                    </div>
                    <div className="mt-2 space-y-1">
                      <div 
                        className="text-xs px-2 py-1 rounded" 
                        style={{ 
                          color: themeSettings.sidebar_text,
                          backgroundColor: themeSettings.sidebar_link_active 
                        }}
                      >
                        Active Item
                      </div>
                      <div 
                        className="text-xs px-2 py-1 rounded opacity-80 cursor-pointer transition-colors"
                        style={{ color: themeSettings.sidebar_text }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeSettings.sidebar_link_hover}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        Hover Me
                      </div>
                      <div className="text-xs px-2 py-1 opacity-70" style={{ color: themeSettings.sidebar_text }}>Normal Item</div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 bg-white rounded-lg p-4">
                  <div className="flex gap-2 mb-3">
                    <button 
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ backgroundColor: themeSettings.primary_color }}
                    >
                      Primary Button
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ backgroundColor: themeSettings.secondary_color }}
                    >
                      Secondary
                    </button>
                    <button 
                      className="px-4 py-2 rounded-lg text-white text-sm"
                      style={{ backgroundColor: themeSettings.accent_color }}
                    >
                      Accent
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleSaveTheme}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-theme-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Theme
              </button>
            </div>
          </div>
        )}

        {/* Opportunities Tab */}
        {activeTab === 'opportunities' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Opportunities Settings</h2>
                <p className="text-gray-500 text-sm">Configure how opportunities are displayed to your users</p>
              </div>
            </div>

            {/* Trados Opportunities Toggle */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Display Trados Platform Opportunities</h3>
                  <p className="text-gray-500 text-sm">
                    When enabled, your users will see opportunities from the main Trados platform in addition to your company's own opportunities.
                    This allows your users to access a wider range of trading opportunities.
                  </p>
                </div>
                <button
                  onClick={() => setCompanyFeatureSettings(prev => ({
                    ...prev,
                    show_trados_opportunities: !prev.show_trados_opportunities
                  }))}
                  className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    companyFeatureSettings.show_trados_opportunities 
                      ? 'bg-blue-600' 
                      : 'bg-gray-300'
                  }`}
                  data-testid="toggle-trados-opportunities"
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                      companyFeatureSettings.show_trados_opportunities 
                        ? 'translate-x-7' 
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  {companyFeatureSettings.show_trados_opportunities ? (
                    <>
                      <ToggleRight className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">Enabled</span>
                      <span className="text-sm text-gray-500">— Your users can see Trados platform opportunities</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">Disabled</span>
                      <span className="text-sm text-gray-500">— Only your company's opportunities are shown</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Super Admin Videos Toggle */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Display Platform Educational Videos</h3>
                  <p className="text-gray-500 text-sm">
                    When enabled, your users will see educational videos from the main Trados platform.
                    This includes training materials, tutorials, and informational content provided by Trados.
                  </p>
                </div>
                <button
                  onClick={() => setCompanyFeatureSettings(prev => ({
                    ...prev,
                    show_super_admin_videos: !prev.show_super_admin_videos
                  }))}
                  className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    companyFeatureSettings.show_super_admin_videos 
                      ? 'bg-blue-600' 
                      : 'bg-gray-300'
                  }`}
                  data-testid="toggle-super-admin-videos"
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                      companyFeatureSettings.show_super_admin_videos 
                        ? 'translate-x-7' 
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {/* Status indicator */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  {companyFeatureSettings.show_super_admin_videos ? (
                    <>
                      <ToggleRight className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">Enabled</span>
                      <span className="text-sm text-gray-500">— Your users can see Trados platform educational videos</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">Disabled</span>
                      <span className="text-sm text-gray-500">— Only your company's videos are shown</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
              <button
                onClick={handleSaveFeatureSettings}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
                data-testid="save-opportunities-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Settings
              </button>
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <div className="space-y-6">
            {/* Company Photos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Camera className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Company Photos</h2>
                  <p className="text-gray-500 text-sm">Upload photos of your office, warehouse, products, or team ({profilePhotos.length}/10)</p>
                </div>
              </div>

              {/* Photo Grid */}
              {profilePhotos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
                  {profilePhotos.map((photo, idx) => (
                    <div key={photo.stored_name || idx} className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-square">
                      <img
                        src={resolveFileUrl(photo.url, API_URL)}
                        alt={photo.filename || `Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {isAdmin && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <button
                            onClick={() => handleDeletePhoto(photo.stored_name)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                            title="Delete photo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate">{photo.filename}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mb-6">No photos uploaded yet.</p>
              )}

              {/* Upload Button */}
              {isAdmin && profilePhotos.length < 10 && (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  <Camera className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition">
                    <Upload className="w-4 h-4" />
                    {uploadingPhotos ? 'Uploading...' : 'Upload Photos'}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handlePhotosUpload}
                      disabled={uploadingPhotos}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG, WebP, GIF up to 10MB each. Max 10 photos.</p>
                </div>
              )}
            </div>

            {/* Company Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Company Documents</h2>
                  <p className="text-gray-500 text-sm">Upload brochures, presentations, certifications ({profileDocuments.length}/10)</p>
                </div>
              </div>

              {/* Document List */}
              {profileDocuments.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {profileDocuments.map((doc, idx) => (
                    <div
                      key={doc.stored_name || idx}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 group hover:border-gray-300 transition"
                    >
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(doc.size)}
                          {doc.content_type && ` · ${doc.content_type.split('/').pop().toUpperCase()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={resolveFileUrl(doc.url, API_URL)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-blue-600 transition"
                          title="View document"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteDocument(doc.stored_name)}
                            className="p-2 text-gray-400 hover:text-red-600 transition"
                            title="Delete document"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mb-6">No documents uploaded yet.</p>
              )}

              {/* Upload Button */}
              {isAdmin && profileDocuments.length < 10 && (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition">
                    <Upload className="w-4 h-4" />
                    {uploadingDocuments ? 'Uploading...' : 'Upload Documents'}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip"
                      multiple
                      onChange={handleDocumentsUpload}
                      disabled={uploadingDocuments}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PDF, Word, Excel, PowerPoint, and other document formats up to 10MB each. Max 10 documents.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Theme Preview Modal */}
        {previewTheme && (themePresets[previewTheme] || THEME_PRESETS[previewTheme]) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {(themePresets[previewTheme] || THEME_PRESETS[previewTheme]).name}
                    </h3>
                    <p className="text-gray-500 text-sm mt-1">
                      {(themePresets[previewTheme] || THEME_PRESETS[previewTheme]).description}
                    </p>
                  </div>
                  <button 
                    onClick={() => setPreviewTheme(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="p-6 bg-gray-50">
                <div className="flex gap-4">
                  {/* Sidebar Preview */}
                  <div 
                    className="w-56 h-64 rounded-xl flex flex-col shadow-lg overflow-hidden"
                    style={{ backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_bg }}
                  >
                    <div className="p-4 border-b border-white/10">
                      <div 
                        className="text-lg font-bold"
                        style={{ color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_text }}
                      >
                        Your Company
                      </div>
                    </div>
                    <div className="p-3 flex-1">
                      <div 
                        className="px-3 py-2 rounded-lg text-sm mb-1"
                        style={{ 
                          backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_link_active,
                          color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_text
                        }}
                      >
                        <Check className="w-4 h-4 inline mr-2" />
                        Dashboard (Active)
                      </div>
                      <div 
                        className="px-3 py-2 rounded-lg text-sm mb-1 opacity-80"
                        style={{ 
                          backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_link_hover,
                          color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_text
                        }}
                      >
                        Settings (Hover)
                      </div>
                      <div 
                        className="px-3 py-2 text-sm opacity-70"
                        style={{ color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_text }}
                      >
                        Users
                      </div>
                      <div 
                        className="px-3 py-2 text-sm opacity-70"
                        style={{ color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.sidebar_text }}
                      >
                        Billing
                      </div>
                    </div>
                  </div>

                  {/* Main Content Preview */}
                  <div className="flex-1 space-y-4">
                    {/* Header Preview */}
                    <div 
                      className="rounded-xl p-4 shadow"
                      style={{ 
                        backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.external_header_bg,
                        color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.external_header_text
                      }}
                    >
                      <span className="font-semibold">Header Area</span>
                    </div>

                    {/* Buttons Preview */}
                    <div className="bg-white rounded-xl p-4 shadow">
                      <p className="text-sm text-gray-500 mb-3">Button Colors</p>
                      <div className="flex gap-2 flex-wrap">
                        <button 
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                          style={{ backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.primary_color }}
                        >
                          Primary
                        </button>
                        <button 
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                          style={{ backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.secondary_color }}
                        >
                          Secondary
                        </button>
                        <button 
                          className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                          style={{ backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.accent_color }}
                        >
                          Accent
                        </button>
                      </div>
                    </div>

                    {/* Footer Preview */}
                    <div 
                      className="rounded-xl p-3 shadow text-sm"
                      style={{ 
                        backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.external_footer_bg,
                        color: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.external_footer_text
                      }}
                    >
                      Footer Area
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 bg-white flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  <Sparkles className="w-4 h-4 inline mr-1 text-amber-500" />
                  You can still customize individual colors after applying
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPreviewTheme(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => applyPresetTheme(previewTheme)}
                    className="px-4 py-2 text-white rounded-lg font-medium transition flex items-center gap-2"
                    style={{ backgroundColor: (themePresets[previewTheme] || THEME_PRESETS[previewTheme]).colors.primary_color }}
                    data-testid="apply-theme-btn"
                  >
                    <Check className="w-4 h-4" />
                    Apply Theme
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default CompanySettings;
